import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtMoney, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CreditCard, Plus, Trash2, Edit2, FileDown, Calendar } from "lucide-react";
import { toast } from "sonner";

const FREQUENCIES = [
  { v: "monthly", label: "Monthly" },
  { v: "quarterly", label: "Quarterly" },
  { v: "annual", label: "Annual" },
];

const FREQ_COLOR = {
  monthly: "bg-blue-100 text-blue-800 border-blue-200",
  quarterly: "bg-violet-100 text-violet-800 border-violet-200",
  annual: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function Billing() {
  const [tab, setTab] = useState("schedule");
  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Billing</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Billing schedule</h1>
        <p className="text-slate-500 text-sm mt-1">
          Monthly, quarterly and annual billing cycles per account — see what to invoice each month and export to CSV.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1">
          {[["schedule", "Schedule"], ["profiles", "Billing profiles"]].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`billing-tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="schedule" className="mt-6"><Schedule /></TabsContent>
        <TabsContent value="profiles" className="mt-6"><Profiles /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Schedule (12-month grid + CSV) ---------------- */
function Schedule() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/schedule", { params: { year } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const downloadCsv = async () => {
    try {
      const r = await fetch(`${API}/billing/schedule.csv?year=${year}`, { credentials: "include" });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing_schedule_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const yearOptions = [];
  for (let y = currentYear - 1; y <= currentYear + 3; y++) yearOptions.push(y);

  if (loading || !data) return <div className="text-slate-500 font-mono text-sm">Loading schedule…</div>;

  const months = data.months || [];
  const maxTotal = Math.max(...months.map((m) => m.total), 1);

  return (
    <div className="space-y-5" data-testid="billing-schedule">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 font-mono uppercase tracking-wider">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="billing-year"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Annual total</div>
            <div className="font-heading text-2xl font-bold text-slate-900">{fmtMoney(data.annual_total)}</div>
          </div>
          <Button data-testid="billing-csv-btn" onClick={downloadCsv} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <FileDown className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {months.map((m) => {
          const pct = (m.total / maxTotal) * 100;
          const isCurrent = m.month === new Date().getMonth() + 1 && year === currentYear;
          return (
            <Card
              key={m.month}
              className={`border-slate-200 ${isCurrent ? "ring-2 ring-blue-500" : ""}`}
              data-testid={`billing-month-${m.month}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-heading font-semibold text-slate-900">{m.label}</div>
                  {isCurrent && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Now</span>}
                </div>
                <div className="font-heading text-2xl font-bold mt-1">{fmtMoney(m.total)}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">{m.items.length} bill{m.items.length === 1 ? "" : "s"}</div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                </div>
                {m.items.length > 0 && (
                  <ul className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                    {m.items.slice(0, 6).map((it, i) => (
                      <li key={i} className="text-[11px] text-slate-700 flex justify-between gap-2">
                        <span className="truncate">{it.account_name}</span>
                        <span className="font-mono text-slate-900">{fmtMoney(it.amount)}</span>
                      </li>
                    ))}
                    {m.items.length > 6 && (
                      <li className="text-[10px] font-mono text-slate-400">+{m.items.length - 6} more…</li>
                    )}
                  </ul>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" /> Profile contribution ({(data.profile_summary || []).length} active)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(data.profile_summary || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No profiles bill in {year} yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Account · Profile</th>
                  <th className="px-3 py-2">Frequency</th>
                  <th className="px-3 py-2 text-right">Events</th>
                  <th className="px-3 py-2 text-right">{year} total</th>
                </tr>
              </thead>
              <tbody>
                {(data.profile_summary || []).map((p) => (
                  <tr key={p.profile_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="text-xs text-slate-500">{p.account_name}</div>
                      <div className="font-medium text-slate-900">{p.name}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${FREQ_COLOR[p.frequency]}`}>
                        {p.frequency}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{p.events}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(p.annual_billed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- Profiles list / CRUD ---------------- */
function Profiles() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/billing-profiles"), api.get("/accounts")]);
    setItems(r.data);
    setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this billing profile?")) return;
    await api.delete(`/billing-profiles/${id}`);
    load();
  };

  const toggleActive = async (p) => {
    await api.patch(`/billing-profiles/${p.id}`, { active: !p.active });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} billing profiles</div>
        <Button data-testid="new-billing-btn" onClick={() => setEditing({})} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New profile
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <CreditCard className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No billing profiles yet</div>
          <div className="text-sm text-slate-500 mt-1">Create a profile per account to track recurring invoices.</div>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="billing-profiles-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Account · Profile</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Start</th>
                <th className="px-4 py-3">End</th>
                <th className="px-4 py-3 text-center">Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!p.active ? "opacity-60" : ""}`} data-testid={`billing-row-${p.id}`}>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => nav(`/app/accounts/${p.account_id}`)}>
                    <div className="text-xs text-slate-500 hover:text-blue-700">{p.account_name}</div>
                    <div className="font-medium text-slate-900">{p.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${FREQ_COLOR[p.frequency]}`}>
                      {p.frequency}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-medium">{fmtMoney(p.amount)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(p.start_date)}</td>
                  <td className="px-4 py-3 text-slate-600">{p.end_date ? fmtDate(p.end_date) : "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(p)}>
                      <Edit2 className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p.id)}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editing && (
        <BillingProfileDialog
          profile={editing}
          accounts={accounts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BillingProfileDialog({ profile, accounts, onClose, onSaved }) {
  const isNew = !profile.id;
  const [form, setForm] = useState({
    account_id: profile.account_id || "",
    name: profile.name || "",
    frequency: profile.frequency || "annual",
    amount: profile.amount ?? 0,
    currency: profile.currency || "GBP",
    start_date: profile.start_date || new Date().toISOString().slice(0, 10),
    end_date: profile.end_date || "",
    active: profile.active !== false,
    notes: profile.notes || "",
  });

  const save = async () => {
    if (!form.account_id) return toast.error("Account required");
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.start_date) return toast.error("Start date required");
    const payload = {
      ...form,
      amount: Number(form.amount) || 0,
      end_date: form.end_date || null,
      notes: form.notes || null,
    };
    try {
      if (isNew) await api.post("/billing-profiles", payload);
      else await api.patch(`/billing-profiles/${profile.id}`, payload);
      toast.success(isNew ? "Billing profile created" : "Billing profile updated");
      onSaved();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isNew ? "New" : "Edit"} billing profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Account *</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })} disabled={!isNew}>
              <SelectTrigger data-testid="billing-form-account"><SelectValue placeholder="Select account…" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Profile name *</Label>
            <Input data-testid="billing-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cyber Security Stax — annual" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Frequency *</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="billing-form-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount per cycle (£) *</Label>
              <Input data-testid="billing-form-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date *</Label>
              <Input data-testid="billing-form-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End date (optional)</Label>
              <Input data-testid="billing-form-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <Label className="text-sm">Active</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="billing-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">
            {isNew ? "Create profile" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
